/* global window */

import videojs from 'video.js';
import 'videojs-contrib-ads';
import 'videojs-ima';
import 'videojs-overlay';
import 'videojs-markers';
import 'videojs-dock';

import './playlists';
import './playlistControls';
import './playlistControls/overlay';

import get from 'lodash/get';
import isMobileJS from 'ismobilejs';

import Player from './Player';
import AdTag from './AdTag';
import AmazonBid from './AmazonBid';
import Anime from './Anime';
import Youbora from './Youbora';
import VideoEmbed from './VideoEmbed';
import NowcastEmbed from './NowcastEmbed';
import EventTracker from './EventTracker';
import RecTracker from './RecTracker';
import DAITracker from './DAITracker';
import { attachEvents } from './events';
import {
  resizePoster,
  generateRandomId,
  isBrowserEdge,
} from './utils';
import { evaluateSettings } from './evaluateSettings';
import {
  adsManagerLoadedCallback,
  isAdsLoaderReady,
} from './ads';
import { isStickyActive } from './sticky';

/**
 * HTV version of the player instance.
 */
export default class Encore extends Player {
  /**
   * Create an Encore video player instance.
   *
   * @param {HTMLElement} el - Player element.
   * @param {Object} settings - Settings object.
   */
  constructor(el, settings) {
    super(el, settings);

    this.bid = new AmazonBid(this.playerElementId, this.settings);
    this.eventTracker = new EventTracker();
    this.recTracker = new RecTracker();
    this.previousSource = null;
    this.inViewPlayTriggered = false;
    this.initialEvent = null;
  }

  /**
   * The Video Embed "data-player-id" attribute.
   * @return {string}
   */
  get embedId() {
    return this.el.getAttribute('data-player-id');
  }

  /**
   * The Video Embed "id" attribute.
   * @return {string}
   */
  get widgetId() {
    if (!this.el.hasAttribute('id')) {
      this.el.setAttribute('id', generateRandomId());
    }

    return this.el.getAttribute('id');
  }

  /**
   * Returns the ID of the root player DOM element.
   * @return {string}
   */
  get playerElementId() {
    return `${this.settings.playerClass}-${this.embedId}-${this.widgetId}`;
  }

  /**
   * Returns the dock status from the video DOM element.
   * @return {boolean}
   */
  get isDockEnabled() {
    const dockAttr = this.el.getAttribute('data-player-dock') || false;

    const dockEnabled = dockAttr === '1' ||
      dockAttr === 'true' ||
      dockAttr === 1 ||
      dockAttr === true;

    return dockEnabled;
  }

  /**
   * Returns the autoplay status from the video DOM element.
   * @return {boolean}
   */
  get shouldAutoplayEmbed() {
    const embedAutoplayOverride = this.el.getAttribute('data-player-autoplay') || true;

    const shouldAutoplayEmbed = embedAutoplayOverride === '1' ||
      embedAutoplayOverride === 'true' ||
      embedAutoplayOverride === 1 ||
      embedAutoplayOverride === true;

    return shouldAutoplayEmbed;
  }

  /**
   * Returns the autoplay status from the current source.
   * @return {boolean}
   */
  get shouldAutoplaySource() {
    const shouldAutoplaySource = this.source.autoplay === '1' ||
      this.source.autoplay === 'true' ||
      this.source.autoplay === 1 ||
      this.source.autoplay === true;

    return shouldAutoplaySource;
  }

  /**
   * Returns the combined autoplay status from embed, source and browser settings.
   * @return {boolean}
   */
  get shouldAutoplay() {
    return this.shouldAutoplayEmbed && this.shouldAutoplaySource;
  }

  /**
   * Returns a new Ad Tag URL.
   * @return {string}
   */
  newAdTagUrl() {
    const adsEnabled = this.evaluatedSettings.ads;

    if (!adsEnabled) {
      return null;
    }

    if (this.source.hasAdTagOverride) {
      return this.source.adTagOverride;
    }

    const adTag = new AdTag(this.player, this.source, this.bid, this.settings);

    return adTag.url;
  }

  /**
   * Set a new player source.
   * @param {Object} source - Video Embed object.
   */
  setSource(source) {
    this.previousSource = this.source;
    this.source = source;
  }

  /**
   * The source (asset) this player should display, with the
   * attributes needed for the player to function.
   * @return {Object} An Embed object.
   */
  getSource() {
    if (this.el.hasAttribute('data-nowcast-dai-asset-id')) {
      const nowcast = new NowcastEmbed(this.embedId, this.settings, {
        isDaiEnabled: this.el.hasAttribute('data-nowcast-dai-enabled') || false,
        daiAssetId: this.el.getAttribute('data-nowcast-dai-asset-id') || '',
      });

      if (nowcast.isDaiEnabled) {
        const daiTracker = new DAITracker(this);
        nowcast.setDaiTracker(daiTracker);
      }

      return nowcast;
    }

    return new VideoEmbed(this.embedId, this.settings);
  }

  /**
   * Retrieve video embed asset data and insert video player.
   */
  load() {
    this.source = this.getSource();
    this.appendVideoElement(this.playerElementId);
    this.player = videojs(this.playerElementId, this.source.techSettings);

    const videoNotAvailableError = 'Sorry, this video is not available, please check back later.';
    const playbackAbortedError = 'Please refresh the page and hit play to resume the stream.';
    videojs.addLanguage('en', {
      'No compatible source was found for this media.': videoNotAvailableError,
      'The media could not be loaded, either because the server or network failed or because the format is not supported.': videoNotAvailableError,
      'The media playback was aborted due to a corruption problem or because the media used features your browser did not support.': playbackAbortedError,
    });

    if (!this.embedId) {
      this.player.fluid(true);
      this.player.error(videoNotAvailableError);
      return Promise.reject(new Error(videoNotAvailableError));
    }

    this.bid.setSlotId(this.source.slotId);

    return Promise.all([this.bid.request(), this.source.load()])
      .then(() => {
        this.configure(this.player);
        return Promise.resolve(this);
      })
      .catch(error => {
        videojs.log.error(error);
        return Promise.reject(new Error('Failed to load player'));
      });
  }

  /**
   * Configure the player. This should be overriden by subclasses
   * in order to configure asset-specific behavior.
   * @param {Object} player - A VideoJS player object.
   */
  configure(player) {
    const { imaSdkAvailable, adBlockDetected } = this.settings;
    this.evaluatedSettings = evaluateSettings(this.el, this.source);

    this.setPlayerAttributes(player);
    this.setPlayerPreviewImage(player);
    this.setPlayerEvents(player);
    this.setPlayerAds(player);
    this.setPlayerMarkers(player);

    if (!this.source.isPlaylist && (!imaSdkAvailable || adBlockDetected)) {
      const { src } = this.source;
      player.src(src);
    }

    if (this.source.isPlaylist) {
      this.setPlayerPlaylistControls(player);
    }

    const youbora = new Youbora();
    youbora.attachPlayer(player,
      this.source.isLiveStream,
      this.source.adTagUrl,
      !this.source.isPlaylist ? this.source.src : []);

    videojs.log(`${this.playerElementId} autoplay: embed = [${this.shouldAutoplayEmbed}] source = [${this.shouldAutoplaySource}]`);

    player.trigger('autoplayStatus');
  }

  /**
   * Set up the markers plugin.
   * @param {Object} player - VideoJS player instance.
   */
  setPlayerMarkers(player) {
    player.markers({
      markerStyle: {
        width: '6px',
        'background-color': '#fecb2f',
      },
      markerTip: {
        display: false,
      },
    });
  }

  /**
   * Set basic player controls.
   * @param {Object} player - VideoJS player instance.
   */
  setPlayerControls(player) {
    const { controls } = this.evaluatedSettings;

    player.controls(!!controls);
    player.autoplay(false);
    player.preload(false);
  }

  /**
   * Set the preview image for single video embeds.
   * @param {Object} player - VideoJS player instance.
   */
  setPlayerPreviewImage(player) {
    const autoplay = this.settings.canAutoplay && this.shouldAutoplay;

    if (!autoplay && this.source.isPlaylist) {
      const playlistPreview = get(this.source.videos, '0.attributes.cropped_preview_image');
      if (playlistPreview) {
        player.poster(resizePoster(playlistPreview));
      }
      return;
    }

    if (!autoplay && this.recTracker.index === 0) {
      if (!this.source.hasAnime) {
        const poster = resizePoster(this.source.poster);
        player.poster(poster);
        return;
      }

      const anime = new Anime(this.source.videoId);
      anime.check()
        .then(result => {
          if (result) {
            player.poster(anime.url);
            setTimeout(() => player.poster(this.source.poster), 5000);
          } else {
            const poster = resizePoster(this.source.poster);
            player.poster(poster);
          }
        });
    }
  }

  /**
   * Set player attributes.
   * @param {Object} player - A VideoJS player object.
   */
  setPlayerAttributes(player) {
    this.setPlayerVolume(player);
    this.setPlayerLayout(player);
    this.setPlayerControls(player);

    if (this.source.hasCaptions) {
      player.addRemoteTextTrack(this.source.textTrack, false);
    }
  }

  /**
   * Set player ad attributes that are needed for proper handling by
   * the IMA SDK.
   * @param {Object} player - A VideoJS player object.
   */
  setPlayerAds(player) {
    const { imaSdkAvailable, adBlockDetected } = this.settings;
    if (!imaSdkAvailable || adBlockDetected) {
      return;
    }

    const adsRenderingSettings = get(this.settings, 'adSettings.adsRenderingSettings', {});
    if (isBrowserEdge(window.navigator)) {
      adsRenderingSettings.mimeTypes = ['video/mp4'];
    }

    const imaOptions = {
      id: this.playerElementId,
      adsManagerLoadedCallback: adsManagerLoadedCallback.bind(this),
      adsRenderingSettings,
      contribAdsSettings: get(this.settings, 'adSettings.contribAdsSettings', {}),
      showCountdown: false,
      disableCustomPlaybackForIOS10Plus: isMobileJS.apple.device,
      vastLoadTimeout: get(this.settings, 'adSettings.vastLoadTimeout', 5000),

      // hearstPlayer.instances[0].player.ima.controller.sdkImpl.adsLoader.getSettings().K
      vpaidMode: get(this.settings, 'adSettings.vpaidMode', 1),
    };

    player.ima(imaOptions);
  }

  /**
   * Set the player source so that we can still load/not load the
   * player based on the presense of the IMA SDK.
   * @param {Object} player - A VideoJS player object.
   */
  setPlayerSrc(player) {
    if (this.source.isPlaylist) {
      this.setPlayerPlaylistSrc(player);
      return;
    }

    const { src } = this.source;
    const { imaSdkAvailable, adBlockDetected } = this.settings;

    if (!src.length) {
      throw new Error('Nitehawk souce not found.');
    }

    if (imaSdkAvailable && !adBlockDetected) {
      const adTagUrl = this.newAdTagUrl();
      videojs.log(`${this.playerElementId} adTagUrl = [${adTagUrl}]`);

      this.source.setAdTagUrl(adTagUrl);
      this.eventTracker.clear();

      player.ima.setContentWithAdTag(src, adTagUrl);
    } else {
      player.src(src);
    }
  }

  /**
   * Register the playlist controls plugin.
   * @param {Object} player - A VideoJS player object.
   */
  setPlayerPlaylistControls(player) {
    if (!this.source.isPlaylist) {
      return;
    }

    const defaultOptions = {
      videos: this.getPlaylistSrc(),
    };

    if (!isMobileJS.any) {
      player.playlistControls(defaultOptions);
    }
    player.playlistOverlay(defaultOptions);

    player.dock({});

    player.one('play', () => {
      player.shelf.addChild('PlaylistOverlayButton');
    });
  }

  /**
   * Helper for grabbing values out of the playlist's videos for use by plugins.
   * @return {Object}
   */
  getPlaylistSrc() {
    return this.source.videos.map(video => {
      const item = {
        sources: video.src,
        thumbnail: isBrowserEdge(window.navigator) ? this.getPoster(video) : video.thumbnail,
        title: video.title,
        customPosterSetter: player_ => player_.poster(this.getPoster(video)),
      };

      const adTag = this.getAdTag(video);
      if (adTag) {
        item.customSrcSetter = (player_, item_) => {
          const adTagUrl = this.newAdTagUrl();
          videojs.log(`${this.playerElementId} adTagUrl = [${adTagUrl}]`);

          this.source.setAdTagUrl(adTagUrl);
          this.eventTracker.clear();
          this.player.trigger('ads-ima-adRequest');

          player_.ima.setContentWithAdTag(item_.sources, adTagUrl, false);
          player_.ima.requestAds();
        };
      }

      return item;
    });
  }

  /**
   * Returns the ad start event to listen on.
   */
  getAdStartEvent() {
    if (isMobileJS.any) {
      return 'touchend';
    }

    if (!this.settings.canAutoplay || !this.shouldAutoplay) {
      return 'click';
    }

    if (isStickyActive()) {
      if (this.el.getAttribute('id') === 'w0') {
        window.hearstPlayer.incFirstVideoEmbed(this.playerElementId);
      }

      const adStartEvent = window.hearstPlayer.isFirstVideoEmbed(this.playerElementId) ? 'inViewPlay' : 'click';
      return adStartEvent;
    }

    return 'inViewPlay';
  }

  /**
   * Ad start event handler.
   * @param {Object} player - A VideoJS player object.
   * @param {string} initialEvent - Initial user event.
   */
  adStartHandler(player, initialEvent) {
    player.off('click');
    player.off('touchend');

    this.initialEvent = initialEvent;
    videojs.log(`${this.playerElementId} initialEvent = [${initialEvent}]`);

    player.ima.initializeAdDisplayContainer();

    isAdsLoaderReady(player)
      .then(() => {
        videojs.log(`${this.playerElementId} ads-ima-adRequest`);
        player.trigger('ads-ima-adRequest');

        this.setPlayerSrc(player);
        player.ima.requestAds();
      });
  }

  /**
   * Overrides existing setPlayerEvents to add custom events.
   * @param {Object} player - A VideoJS player object.
   */
  setPlayerEvents(player) {
    const { imaSdkAvailable, adBlockDetected } = this.settings;
    const adStartEvent = this.getAdStartEvent();

    videojs.log(`${this.playerElementId} adStartEvent = [${adStartEvent}]`);

    // We only want to register basic ad events when we have the IMA SDK.
    if (imaSdkAvailable && !adBlockDetected) {
      player.one(adStartEvent,
        this.adStartHandler.bind(this, player, adStartEvent));

      if (adStartEvent === 'click') {
        player.one('touchend',
          this.adStartHandler.bind(this, player, 'touchend'));
      }
    }

    this.source.setAdStartEvent(adStartEvent);

    attachEvents(this);
  }
}
